import { FieldValues, useForm } from 'react-hook-form';
import { Template } from './Template';
import { FormItem } from '@/components/dashboard/settings';
import InputPassword from '@/components/form/input-password/input-password';
import { ToggleCustom, Typography } from '@/components/common';
import { passwordCriteria } from '@/const';
import { useNotification } from '@/hooks';
import { userLawyerService, userService } from '@/api';
import { PasswordPayload, UserI } from '@/interfaces';
import * as React from 'react';
import { formatErrorMessage } from '@/lib';

interface CredentialsSettingsProps {
	userInformation?: UserI;
	refetch: () => void;
}

export const CredentialsSettings: React.FC<CredentialsSettingsProps> = ({
	userInformation,
	refetch,
}) => {
	const { toastSuccess, toastError } = useNotification();
	const [disabled, setDisabled] = React.useState<boolean>(false);
	const {
		register,
		watch,
		reset,
		formState: { errors },
		handleSubmit,
		setValue,
	} = useForm({ mode: 'onChange' });

	const twoFactor = watch('two_factor');

	React.useEffect(() => {
		if (userInformation) {
			setValue('two_factor', userInformation.is_2fa_actived);
		}
	}, [userInformation]);

	React.useEffect(() => {
		if (
			userInformation &&
			twoFactor !== undefined &&
			twoFactor !== userInformation.is_2fa_actived
		) {
			handleUpdateTwoFactorAuth();
		}
	}, [twoFactor]);

	const handleSave = async (data: FieldValues): Promise<void> => {
		try {
			if (
				userInformation?.social_type === 'local' &&
				!!data.password &&
				!!data.new_password &&
				!!data.password_confirm
			) {
				const newData: PasswordPayload = {
					confirmPassword: data.password_confirm,
					currentPassword: data.password,
					password: data.new_password,
				};
				if (userInformation.role === 'lawyer') {
					await userLawyerService.updatePassword(newData);
				} else {
					await userService.updatePassword(newData);
				}
				toastSuccess('Your changes have been saved successfully.');
				resetValues();
			}

			if (userInformation?.is_2fa_actived !== data.two_factor) {
				await handleUpdateTwoFactorAuth();
			}
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		}
	};

	const handleUpdateTwoFactorAuth = async (): Promise<void> => {
		try {
			await userService.update2FA();
			refetch();
			toastSuccess('Your changes have been saved successfully.');
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		}
	};

	const resetValues = (): void => {
		reset({
			password: undefined,
			new_password: undefined,
			password_confirm: undefined,
			two_factor: undefined,
		});
		setValue('password', '');
		setValue('new_password', '');
		setValue('password_confirm', '');
		setValue('two_factor', false);
		setDisabled(false);
	};

	const handleCancel = (): void => {
		resetValues();
	};
	const commonMessage =
		'Your password does not meet the minimum requirements. It must be at least 8 characters long and include one number, one uppercase letter, and one special character (_-:*).';
	const rules = {
		new_password: {
			minLength: {
				value: 7,
				message: commonMessage,
			},
			validate: (value: string) =>
				watch('password_confirm') && value !== watch('password_confirm')
					? `Passwords don't match`
					: undefined,
		},
		password_confirm: {
			maxLength: {
				value: 30,
				message: commonMessage,
			},
			minLength: {
				value: 7,
				message: commonMessage,
			},

			validate: (value: string) =>
				watch('new_password') && value !== watch('new_password')
					? `The passwords do not match. Please ensure they are identical.`
					: undefined,
		},
	};

	const isDisabledButton = (): void => {
		if (userInformation && !userInformation.phonenumber) {
			setDisabled(true);
			setValue('two_factor', false);
		} else if (userInformation && !userInformation.is_phone_number_verified) {
			setDisabled(true);
			setValue('two_factor', false);
		} else {
			setDisabled(false);
		}
	};
	const isError = Object.keys(errors).length > 0;

	return (
		<Template
			title="Account Access Details"
			onClickSave={handleSubmit(handleSave)}
			onClickCancel={handleCancel}
		>
			{userInformation?.social_type === 'local' && (
				<Typography type="semibold-18" className="mt-6 mb-1">
					Change Password
				</Typography>
			)}
			{userInformation?.social_type === 'local' ? (
				<>
					<FormItem label="Current password">
						<InputPassword
							name="password"
							validate={false}
							register={register}
							className="max-w-[512px]"
							classNameIcon={'!top-[23%]'}
							classNameInput="!m-0"
						/>
					</FormItem>
					<FormItem label="New password" className="!items-start space-y-0">
						<InputPassword
							name="new_password"
							title="Password"
							register={register}
							validate={true}
							className="max-w-[512px]"
							classNameIcon={'!top-[53%]'}
							rules={rules.new_password}
							error={errors.new_password}
							customErrorMessage={commonMessage}
						/>
						<Typography type="regular-12" className="text-opacity-black my-4">
							{!isError ? passwordCriteria : ''}
						</Typography>
						<InputPassword
							name="password_confirm"
							title="Confirm Password"
							register={register}
							validate={false}
							className="max-w-[512px]"
							classNameIcon={'!top-[53%]'}
							rules={rules.password_confirm}
							error={errors.password_confirm}
						/>
					</FormItem>
				</>
			) : null}
			{userInformation?.role !== 'lawyer' && (
				<Typography type="semibold-18" className="mt-8 mb-1">
					Enable Two-Step Verification
				</Typography>
			)}
			{userInformation?.role !== 'lawyer' && (
				<FormItem
					label="Add Extra Security to Your Account"
					className="!items-baseline space-y-0 !border-none"
				>
					<ToggleCustom
						name="two_factor"
						register={register}
						label="Two factor authentication."
						watch={watch}
						setValue={setValue}
						setDisabled={setDisabled}
						disabled={disabled}
						isDisabledButton={isDisabledButton}
						className="!mt-0"
						phoneNumber={userInformation?.phonenumber || ''}
						refetch={refetch}
					/>
				</FormItem>
			)}
		</Template>
	);
};
